import { ChangeEvent, ReactNode, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import { Lead, Panel, PanelHeader, PanelBody } from '../ui/Panel';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Pill from '../ui/Pill';
import { EmptyState, ErrorNotice, InfoNotice, SkeletonRows } from '../ui/States';
import { errorMessage, inputClass, label } from '../../lib/programs';
import { SendSteps } from './SendSteps';
import ChangesTable, { sentNote } from './ChangesTable';
import { promptAction } from '../ui/Confirm';
import {
  HotelPerson,
  HotelView,
  IncreaseRequest,
  ReturnedRow,
  ReturnedSummary,
  em,
  headRow,
  monthDay,
  num,
  td,
  th,
  toBase64,
  weekDay,
  when,
} from './logistics';

const FIELD_LABELS: Record<string, string> = {
  checkIn: 'check-in',
  checkOut: 'check-out',
  notes: 'notes',
  note: 'note',
  email: 'email',
  assistantEmail: 'assistant',
  firstName: 'first name',
  lastName: 'last name',
};
const ON_LIST: Record<string, [string, 'healthy' | 'problem' | 'planned']> = {
  yes: ['On the list', 'healthy'],
  dates_differ: ['Dates differ', 'problem'],
  missing: ['Not on the list', 'problem'],
  not_expected: ['On the list, no room here', 'planned'],
};
const RETURNED_LABEL: Record<ReturnedRow['status'], string> = {
  ok: 'Same',
  dates_differ: 'Dates differ',
  missing_in_hotel: 'Not on the hotel’s list',
  missing_in_platform: 'Not on the platform',
  not_expected: 'On the hotel’s list, no room on the platform',
};
const PEOPLE_SHOWN = 25;

// Tables that stack on a phone: one block per row, each cell on its own line with its column name.
const stackTable = 'w-full tabular max-md:block';
const stackRow = 'border-b border-line max-md:block max-md:py-3';
const stackCell = `${td} max-md:block max-md:py-[2px] max-md:pr-0`;
function Col({ children }: { children: ReactNode }) {
  return <span className="md:hidden text-small text-ink-3">{children} · </span>;
}

function Field({
  label: text,
  hint,
  wide,
  children,
}: {
  label: string;
  hint?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`block ${wide ? 'md:col-span-2' : ''}`}>
      <span className="block text-[15px] leading-5 font-semibold text-ink mb-[6px]">{text}</span>
      {children}
      {hint ? <span className="block text-small text-ink-3 mt-1">{hint}</span> : null}
    </label>
  );
}
const check = 'w-[18px] h-[18px] accent-accent';
const grid2 = 'grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4';

function HotelDetailsModal({
  eventId,
  view,
  onClose,
  onSaved,
}: {
  eventId: string;
  view: HotelView;
  onClose: () => void;
  onSaved: () => void;
}) {
  const nights = view.hotel.blockNights;
  const [form, setForm] = useState({
    name: view.hotel.name ?? '',
    blockSize: view.hotel.blockSize == null ? '' : String(view.hotel.blockSize),
    blockFirstNight: nights[0] ?? '',
    blockLastNight: nights[nights.length - 1] ?? '',
    contactEmail: view.hotel.contactEmail ?? '',
    checkOutDay: view.hotel.checkOutDay ?? '',
    incidentals: view.hotel.incidentals ?? 'guest',
    altName: view.hotel.alternate?.name ?? '',
    altEmail: view.hotel.alternate?.contactEmail ?? '',
    altReason: view.hotel.alternate?.reason ?? '',
  });
  const save = useMutation(
    async () => {
      const { altName, altEmail, altReason, ...rest } = form;
      return (
        await api.put(`/events/${eventId}/hotel/settings`, {
          ...rest,
          blockSize: form.blockSize === '' ? null : Number(form.blockSize),
          checkOutDay: form.checkOutDay || null,
          alternate: altName.trim()
            ? { name: altName, contactEmail: altEmail || null, reason: altReason || null }
            : null,
        })
      ).data.data;
    },
    { onSuccess: onSaved },
  );
  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });
  return (
    <Modal
      title="Edit hotel details"
      why="The block, where the rooming list goes, and what everyone shares. Room numbers per night follow from these."
      onClose={onClose}
      width={680}
      footer={
        <>
          <Button variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
            Save hotel details
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </>
      }
    >
      {save.error ? (
        <div className="mb-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice>
        </div>
      ) : null}
      <div className={grid2}>
        <Field label="Hotel">
          <input className={inputClass} value={form.name} onChange={set('name')} placeholder="e.g. Schulich ELC" />
        </Field>
        <Field label="Rooms in the block">
          <input className={inputClass} value={form.blockSize} onChange={set('blockSize')} inputMode="numeric" />
        </Field>
        <Field label="First night of the block">
          <input type="date" className={inputClass} value={form.blockFirstNight} onChange={set('blockFirstNight')} />
        </Field>
        <Field label="Last night of the block">
          <input type="date" className={inputClass} value={form.blockLastNight} onChange={set('blockLastNight')} />
        </Field>
        <Field label="Send the rooming list to">
          <input
            type="email"
            className={inputClass}
            value={form.contactEmail}
            onChange={set('contactEmail')}
            placeholder="Hotel contact’s email"
          />
        </Field>
        <Field label="Check-out day for everyone">
          <input type="date" className={inputClass} value={form.checkOutDay} onChange={set('checkOutDay')} />
        </Field>
        <Field label="Incidentals">
          <select className={inputClass} value={form.incidentals} onChange={set('incidentals')}>
            <option value="guest">Guest’s own card</option>
            <option value="master">Master account</option>
          </select>
        </Field>
      </div>
      <details className="mt-5" open={Boolean(view.hotel.alternate)}>
        <summary className="cursor-pointer text-accent underline underline-offset-[3px] hover:text-ink">
          Overflow hotel
        </summary>
        <div className={`${grid2} mt-4`}>
          <Field label="Overflow hotel" hint="If the block fills or there are no adjoining rooms.">
            <input className={inputClass} value={form.altName} onChange={set('altName')} />
          </Field>
          <Field label="Overflow hotel contact">
            <input type="email" className={inputClass} value={form.altEmail} onChange={set('altEmail')} />
          </Field>
          <Field label="Why the overflow" wide>
            <input className={inputClass} value={form.altReason} onChange={set('altReason')} />
          </Field>
        </div>
      </details>
    </Modal>
  );
}

function RoomsPerNight({
  view,
  canEdit,
  onEdit,
  onNight,
}: {
  view: HotelView;
  canEdit: boolean;
  onEdit: () => void;
  onNight: ((date: string) => void) | null;
}) {
  const p = view.perNight;
  const h = view.hotel;
  const grown = h.increaseRequests.filter((r) => r.grantedRooms && r.answer !== 'pending');
  const firstNight = h.blockNights[0];
  const lastNight = h.blockNights[h.blockNights.length - 1];
  const dd = 'm-0 text-ink';
  const unset = 'm-0 text-ink-3';
  return (
    <Panel>
      <PanelHeader title="Rooms per night" note={h.name ?? 'hotel block'}>
        {canEdit ? <Button onClick={onEdit}>Edit hotel details</Button> : null}
      </PanelHeader>
      <PanelBody>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-12 gap-y-8">
          <div>
            {p.nights.length ? (
              <table className="w-full tabular">
                <thead>
                  <tr className={headRow}>
                    <th className={th}>Night of</th>
                    <th className={`${th} ${num}`}>Rooms</th>
                    <th className={`${th} ${num}`}>In the block</th>
                    <th className={`${th} ${num}`}>To spare</th>
                  </tr>
                </thead>
                <tbody>
                  {p.nights.map((n) => {
                    const peak = n.date === p.peakNight;
                    const outside = !n.inBlock && h.blockNights.length > 0;
                    return (
                      <tr key={n.date} className="border-b border-line">
                        <td className={`${td} ${peak ? 'font-semibold' : ''}`}>
                          {weekDay(n.date)}
                          {outside ? <span className={em}>outside the block</span> : null}
                        </td>
                        <td className={`${td} ${num} ${peak ? 'font-semibold' : ''}`}>
                          {onNight && n.rooms ? (
                            <button
                              type="button"
                              className="text-accent underline underline-offset-[3px] hover:text-ink"
                              aria-label={`${n.rooms} rooms on the night of ${weekDay(n.date)}: show the people`}
                              onClick={() => onNight(n.date)}
                            >
                              {n.rooms}
                            </button>
                          ) : (
                            n.rooms
                          )}
                        </td>
                        <td className={`${td} ${num}`}>
                          {p.blockSize !== null && n.inBlock ? p.blockSize : <span className="text-ink-3">–</span>}
                        </td>
                        <td className={`${td} ${num}`}>
                          {n.over && n.left !== null ? (
                            <Pill tone="problem">{`${-n.left} over`}</Pill>
                          ) : n.left === null ? (
                            <span className="text-ink-3">–</span>
                          ) : (
                            n.left
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <EmptyState title="No rooms yet.">
                {canEdit ? 'Set the block nights in Edit hotel details, or wait' : 'Rooms appear'} when invitees ask for
                a hotel on the registration form.
              </EmptyState>
            )}
            <p className="text-[15px] leading-[22px] text-ink-2 mt-3 max-w-[72ch]">
              {view.counts.requested} {view.counts.requested === 1 ? 'person' : 'people'} asked for a room;{' '}
              {view.counts.rooms} {view.counts.rooms === 1 ? 'room is' : 'rooms are'} on the list.
              {p.outsideBlock
                ? ` ${p.outsideBlock} room-${p.outsideBlock === 1 ? 'night is' : 'nights are'} outside the block.`
                : ''}
              {grown.map((r, i) => (
                <span key={i}>
                  {' '}
                  The block grew by {r.grantedRooms} {r.grantedRooms === 1 ? 'room' : 'rooms'}
                  {r.answeredOn ? ` on ${monthDay(r.answeredOn)}` : ''}.
                </span>
              ))}
            </p>
          </div>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 content-start text-[15px] leading-[22px] max-md:grid-cols-1 max-md:gap-y-0 max-md:[&>dd]:mb-3">
            <dt className="text-small text-ink-3 pt-[1px]">Block</dt>
            {h.blockSize !== null || firstNight ? (
              <dd className={dd}>
                {h.blockSize !== null ? `${h.blockSize} rooms` : 'Rooms not set'}
                {firstNight
                  ? firstNight === lastNight
                    ? `, night of ${monthDay(firstNight)}`
                    : `, nights of ${monthDay(firstNight)} to ${monthDay(lastNight)}`
                  : ''}
              </dd>
            ) : (
              <dd className={unset}>Not set</dd>
            )}
            <dt className="text-small text-ink-3 pt-[1px]">Rooming list goes to</dt>
            {h.contactEmail ? (
              <dd className={`${dd} break-all`}>{h.contactEmail}</dd>
            ) : (
              <dd className="m-0">
                <span className="text-ink-3">Not set.</span>{' '}
                {canEdit ? (
                  <button
                    type="button"
                    className="text-accent underline underline-offset-[3px] hover:text-ink"
                    onClick={onEdit}
                  >
                    Add the hotel contact
                  </button>
                ) : null}
              </dd>
            )}
            <dt className="text-small text-ink-3 pt-[1px]">Check-out for everyone</dt>
            <dd className={h.checkOutDay ? dd : unset}>{h.checkOutDay ? weekDay(h.checkOutDay) : 'Not set'}</dd>
            <dt className="text-small text-ink-3 pt-[1px]">Incidentals</dt>
            <dd className={dd}>{h.incidentals === 'master' ? 'Master account' : 'Guest’s own card'}</dd>
            <dt className="text-small text-ink-3 pt-[1px]">Overflow hotel</dt>
            {h.alternate ? (
              <dd className={dd}>
                {h.alternate.name}
                {h.alternate.contactEmail ? (
                  <span className={`${em} break-all`}>{h.alternate.contactEmail}</span>
                ) : null}
                {h.alternate.reason ? <span className={em}>{h.alternate.reason}</span> : null}
              </dd>
            ) : (
              <dd className={unset}>None</dd>
            )}
          </dl>
        </div>
      </PanelBody>
    </Panel>
  );
}

function RoomModal({
  eventId,
  person,
  sharers,
  alternate,
  onClose,
  onSaved,
}: {
  eventId: string;
  person: HotelPerson;
  sharers: { inviteeId: string; name: string }[];
  alternate: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const r = person.room;
  const [form, setForm] = useState({
    checkIn: r.checkIn ?? person.stay?.checkIn ?? '',
    checkOut: r.checkOut ?? person.stay?.checkOut ?? '',
    none: Boolean(r.none),
    roomType: r.roomType ?? '',
    guest: r.guest ?? '',
    sharesWith: r.sharesWith ?? '',
    assistantEmail: r.assistantEmail ?? '',
    notes: r.notes ?? '',
    confirmed: Boolean(r.confirmed),
    confirmation: r.confirmation ?? '',
    hotel: r.hotel ?? '',
  });
  const queryClient = useQueryClient();
  const save = useMutation(
    async (body: Record<string, unknown>) =>
      (await api.patch(`/events/invitees/${person.inviteeId}/room`, body)).data.data,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['event-hotel', eventId]);
        onSaved();
      },
    },
  );
  const accept = useMutation(
    async () => (await api.post(`/events/invitees/${person.inviteeId}/room/accept-hotel-dates`)).data.data,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['event-hotel', eventId]);
        onSaved();
      },
    },
  );
  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value });
  const submit = () => {
    if (form.none) return save.mutate({ none: true, checkIn: null, checkOut: null });
    const asDefault =
      person.stay?.source === 'default' &&
      form.checkIn === person.stay.checkIn &&
      form.checkOut === person.stay.checkOut;
    const { checkIn, checkOut, ...rest } = form;
    const body = { ...rest, hotel: rest.hotel || null, none: false };
    save.mutate(asDefault ? body : { ...body, checkIn, checkOut });
  };
  const list = person.hotelList;
  return (
    <Modal
      title={person.name}
      why={
        <>
          {person.hotelRequested ? 'Asked for a room on the form.' : 'Did not ask for a room on the form.'}{' '}
          {person.stay?.source === 'default' ? 'The dates below are the default (the block, or the travel rules).' : ''}
        </>
      }
      onClose={onClose}
      width={680}
      footer={
        <>
          <Button variant="primary" disabled={save.isLoading} onClick={submit}>
            Save
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="quiet"
            className="ml-auto max-md:ml-0"
            disabled={save.isLoading}
            onClick={() => save.mutate({ checkIn: null, checkOut: null, none: false })}
          >
            Back to the default dates
          </Button>
        </>
      }
    >
      {save.error ? (
        <div className="mb-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice>
        </div>
      ) : null}
      {accept.error ? (
        <div className="mb-4">
          <ErrorNotice>{errorMessage(accept.error, 'Could not accept the dates.')}</ErrorNotice>
        </div>
      ) : null}
      {list?.present && person.onList === 'dates_differ' ? (
        <div className="mb-4">
          <InfoNotice>
            <span className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span>
                The hotel has {list.checkIn ? monthDay(list.checkIn) : '?'} –{' '}
                {list.checkOut ? monthDay(list.checkOut) : '?'}.
              </span>
              <Button size="sm" disabled={accept.isLoading} onClick={() => accept.mutate()}>
                Use the hotel’s dates
              </Button>
            </span>
          </InfoNotice>
        </div>
      ) : null}
      <div className={grid2}>
        <label className="md:col-span-2 flex items-center gap-2 text-ink">
          <input type="checkbox" className={check} checked={form.none} onChange={set('none')} /> No room needed
        </label>
        <Field label="Check-in">
          <input
            type="date"
            className={inputClass}
            value={form.checkIn}
            onChange={set('checkIn')}
            disabled={form.none}
          />
        </Field>
        <Field label="Check-out">
          <input
            type="date"
            className={inputClass}
            value={form.checkOut}
            onChange={set('checkOut')}
            disabled={form.none}
          />
        </Field>
        {alternate || form.hotel ? (
          <Field label="Hotel" wide>
            <select className={inputClass} value={form.hotel} onChange={set('hotel')} disabled={form.none}>
              <option value="">The block hotel</option>
              <option value="alternate">{alternate ?? 'Overflow hotel'} (overflow)</option>
            </select>
          </Field>
        ) : null}
        <Field label="Room type">
          <select className={inputClass} value={form.roomType} onChange={set('roomType')} disabled={form.none}>
            <option value="">Single</option>
            <option value="Double">Double</option>
          </select>
        </Field>
        <Field label="Shares the room of">
          <select className={inputClass} value={form.sharesWith} onChange={set('sharesWith')} disabled={form.none}>
            <option value="">Nobody (own room)</option>
            {sharers
              .filter((s) => s.inviteeId !== person.inviteeId)
              .map((s) => (
                <option key={s.inviteeId} value={s.inviteeId}>
                  {s.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Guest in the room" hint="Pays the extra fee.">
          <input className={inputClass} value={form.guest} onChange={set('guest')} disabled={form.none} />
        </Field>
        <Field label="Assistant email (cc)">
          <input
            className={inputClass}
            value={form.assistantEmail}
            onChange={set('assistantEmail')}
            disabled={form.none}
          />
        </Field>
        <Field label="Notes for the hotel" wide>
          <input
            className={inputClass}
            value={form.notes}
            onChange={set('notes')}
            disabled={form.none}
            placeholder="e.g. non-smoking, away from elevators"
          />
        </Field>
        <label className="flex items-center gap-2 text-ink md:pt-8">
          <input type="checkbox" className={check} checked={form.confirmed} onChange={set('confirmed')} /> Room
          confirmed
        </label>
        <Field label="Hotel confirmation number">
          <input className={inputClass} value={form.confirmation} onChange={set('confirmation')} />
        </Field>
      </div>
    </Modal>
  );
}

function hotelDates(checkIn: string | null | undefined, checkOut: string | null | undefined) {
  return checkIn ? `${monthDay(checkIn)} – ${checkOut ? monthDay(checkOut) : '?'}` : null;
}

/** People whose differences with the hotel's saved list are still to settle. */
function differences(view: HotelView) {
  const people = view.people ?? [];
  const differ = people.filter((p) => p.onList === 'dates_differ' || (p.hotelList?.present && !p.stay));
  const missing = people.filter((p) => p.onList === 'missing');
  const spelled = people.filter((p) => p.hotelList?.nameDiffers);
  const hotelOnly = view.returned?.hotelOnly ?? [];
  const noneHere = people.filter((p) => p.hotelList?.present && !p.stay).length;
  return {
    differ,
    missing,
    spelled,
    hotelOnly,
    noneHere,
    total: differ.length + missing.length + spelled.length + hotelOnly.length,
  };
}

function ReturnedList({ eventId, view, onDone }: { eventId: string; view: HotelView; onDone: () => void }) {
  const picker = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<{ dryRun: boolean; rows: ReturnedRow[]; summary: ReturnedSummary } | null>(null);
  const check = useMutation(
    async (confirm: boolean) => {
      if (!file) throw new Error('Choose a file');
      return (
        await api.post(
          `/events/${eventId}/hotel/returned`,
          { fileName: file.name, content: await toBase64(file), confirm },
          { timeout: 120000 },
        )
      ).data.data;
    },
    {
      onSuccess: (data) => {
        setResult(data);
        if (!data.dryRun) {
          setFile(null);
          onDone();
        }
      },
    },
  );
  const diffs = result?.rows.filter((r) => r.status !== 'ok' || r.nameDiffers) ?? [];
  const checking = Boolean(file || result?.dryRun);
  return (
    <Panel>
      <PanelHeader title="The hotel’s list">
        <Button onClick={() => picker.current?.click()}>
          {view.returned ? 'Upload a newer list' : 'Upload the hotel’s list'}
        </Button>
        <input
          ref={picker}
          type="file"
          accept=".xlsx,.csv"
          className="sr-only"
          tabIndex={-1}
          aria-label="The hotel’s rooming list (.xlsx or .csv)"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setResult(null);
            e.target.value = '';
          }}
        />
      </PanelHeader>
      <PanelBody>
        {view.returned && !(result && !result.dryRun) ? (
          <p className="text-[15px] leading-[22px] text-ink-2 max-w-[72ch]">
            Saved {when(view.returned.at)}
            {view.returned.fileName ? ` from “${view.returned.fileName}”` : ''}: {view.returned.summary.ok} the same,{' '}
            {view.returned.summary.datesDiffer} with other dates, {view.returned.summary.missingInHotel} missing from
            the hotel’s list, {view.returned.summary.missingInPlatform} not on the platform.
          </p>
        ) : !view.returned && !file ? (
          <p className="text-[15px] leading-[22px] text-ink-2 max-w-[72ch]">
            When the hotel sends their rooming list back, upload it here to see what differs.
          </p>
        ) : null}
        <p className="text-small text-ink-3 mt-1">Ask the hotel for the Excel version (.xlsx); a PDF cannot be read.</p>
        {checking && file ? (
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-[15px] text-ink break-all">{file.name}</span>
            {!result ? (
              <Button size="sm" variant="primary" disabled={check.isLoading} onClick={() => check.mutate(false)}>
                {check.isLoading ? 'Reading…' : 'Show the differences'}
              </Button>
            ) : (
              <Button size="sm" variant="primary" disabled={check.isLoading} onClick={() => check.mutate(true)}>
                Save as the hotel’s current list
              </Button>
            )}
            <Button
              size="sm"
              variant="quiet"
              onClick={() => {
                setFile(null);
                setResult(null);
              }}
            >
              Cancel
            </Button>
          </div>
        ) : null}
        {check.error ? (
          <div className="mt-3">
            <ErrorNotice>
              {errorMessage(check.error, (check.error as Error).message || 'Could not read the file.')}
            </ErrorNotice>
          </div>
        ) : null}
        {result ? (
          <div className="mt-4">
            <p className="text-[15px] leading-[22px] text-ink">
              {result.summary.onList} on the hotel’s list: {result.summary.ok} the same, {result.summary.datesDiffer}{' '}
              with other dates, {result.summary.missingInHotel} missing from the hotel’s list,{' '}
              {result.summary.missingInPlatform} not on the platform, {result.summary.nameDiffers} spelled differently.
              {result.dryRun ? ' Not saved yet.' : ' Saved.'}
            </p>
            {result.dryRun ? (
              diffs.length ? (
                <div className="overflow-x-auto mt-3">
                  <table className="w-full tabular">
                    <thead>
                      <tr className={headRow}>
                        <th className={th}>Person</th>
                        <th className={th}>Hotel’s list</th>
                        <th className={th}>Difference</th>
                        <th className={th}>Here</th>
                        <th className={th}>Hotel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diffs.map((r, i) => (
                        <tr key={`${r.key ?? 'h'}${i}`} className="border-b border-line">
                          <td className={td}>{r.name ?? <span className="text-ink-3">–</span>}</td>
                          <td className={td}>
                            {r.nameOnList ?? <span className="text-ink-3">–</span>}
                            {r.nameDiffers ? <span className={em}>spelled differently</span> : null}
                          </td>
                          <td className={td}>{RETURNED_LABEL[r.status]}</td>
                          <td className={td}>
                            {r.platform ? (
                              `${monthDay(r.platform.checkIn)} – ${monthDay(r.platform.checkOut)}`
                            ) : (
                              <span className="text-ink-3">–</span>
                            )}
                          </td>
                          <td className={td}>
                            {hotelDates(r.hotel?.checkIn, r.hotel?.checkOut) ?? <span className="text-ink-3">–</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-[15px] mt-2">The hotel’s list matches the platform.</p>
              )
            ) : null}
          </div>
        ) : null}
        {view.returned && (!result || !result.dryRun) ? (
          <ReturnedActions eventId={eventId} view={view} onDone={onDone} />
        ) : null}
      </PanelBody>
    </Panel>
  );
}

function ReturnedActions({ eventId, view, onDone }: { eventId: string; view: HotelView; onDone: () => void }) {
  const [linkTo, setLinkTo] = useState<Record<string, string>>({});
  const people = view.people ?? [];
  const { differ, missing, spelled, hotelOnly, total } = differences(view);
  const act = useMutation(
    async ({ path, body }: { path: string; body: Record<string, unknown> }) => (await api.post(path, body)).data.data,
    { onSuccess: onDone },
  );
  const accept = (body: Record<string, unknown>) =>
    act.mutate({ path: `/events/${eventId}/hotel/returned/accept`, body });
  if (!total) return <p className="text-[15px] text-ink-2 mt-4">Nothing left to settle with the hotel’s list.</p>;
  const dates = (p: HotelPerson) => hotelDates(p.hotelList?.checkIn, p.hotelList?.checkOut);
  const dash = <span className="text-ink-3">–</span>;
  const actCell = `${stackCell} text-right max-md:text-left max-md:pt-2`;
  return (
    <div className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 mb-3">
        <h3 className="text-[15px] leading-[22px] font-semibold text-ink">Settle the differences · {total}</h3>
        {differ.length ? (
          <Button
            variant="primary"
            className="max-md:w-full"
            disabled={act.isLoading}
            onClick={() => accept({ all: true })}
          >
            {`Accept all ${differ.length} of the hotel’s dates`}
          </Button>
        ) : null}
      </div>
      {act.error ? (
        <div className="mb-3">
          <ErrorNotice>{errorMessage(act.error, 'Could not save.')}</ErrorNotice>
        </div>
      ) : null}
      <table className={stackTable}>
        <thead className="max-md:hidden">
          <tr className={headRow}>
            <th className={th}>Person</th>
            <th className={th}>Difference</th>
            <th className={th}>Here</th>
            <th className={th}>Hotel</th>
            <th className={th}>
              <span className="sr-only">Action</span>
            </th>
          </tr>
        </thead>
        <tbody className="max-md:block">
          {differ.map((p) => (
            <tr key={`d${p.inviteeId}`} className={stackRow}>
              <td className={`${stackCell} font-semibold`}>{p.name}</td>
              <td className={stackCell} data-label="Difference">
                <Col>Difference</Col>
                {p.stay ? 'Other dates' : 'Room at the hotel, none here'}
              </td>
              <td className={stackCell} data-label="Here">
                <Col>Here</Col>
                {p.stay ? stayText(p) : dash}
              </td>
              <td className={stackCell} data-label="Hotel">
                <Col>Hotel</Col>
                {dates(p) ?? dash}
              </td>
              <td className={actCell}>
                <Button size="sm" disabled={act.isLoading} onClick={() => accept({ inviteeIds: [p.inviteeId] })}>
                  Accept
                </Button>
              </td>
            </tr>
          ))}
          {missing.map((p) => (
            <tr key={`m${p.inviteeId}`} className={stackRow}>
              <td className={`${stackCell} font-semibold`}>{p.name}</td>
              <td className={stackCell} data-label="Difference">
                <Col>Difference</Col>
                Not on the hotel’s list
              </td>
              <td className={stackCell} data-label="Here">
                <Col>Here</Col>
                {stayText(p)}
              </td>
              <td className={stackCell} data-label="Hotel">
                <Col>Hotel</Col>
                {dash}
              </td>
              <td className={actCell}>
                <Button
                  size="sm"
                  disabled={act.isLoading}
                  onClick={() => accept({ inviteeIds: [p.inviteeId] })}
                  title="Record that this person has no room"
                >
                  Accept: no room
                </Button>
              </td>
            </tr>
          ))}
          {spelled.map((p) => (
            <tr key={`n${p.inviteeId}`} className={stackRow}>
              <td className={`${stackCell} font-semibold`}>{p.name}</td>
              <td className={stackCell} data-label="Difference">
                <Col>Difference</Col>
                Spelled “{p.hotelList?.nameOnList}”
              </td>
              <td className={`${stackCell} max-md:hidden`}>{dash}</td>
              <td className={`${stackCell} max-md:hidden`}>{dash}</td>
              <td className={actCell}>
                <span className="inline-flex flex-wrap justify-end gap-2 max-md:justify-start">
                  <Button
                    size="sm"
                    disabled={act.isLoading}
                    onClick={() =>
                      act.mutate({ path: `/events/invitees/${p.inviteeId}/name-correction`, body: { use: 'hotel' } })
                    }
                  >
                    Use the hotel’s spelling
                  </Button>
                  <Button
                    size="sm"
                    variant="quiet"
                    disabled={act.isLoading}
                    onClick={() =>
                      act.mutate({ path: `/events/invitees/${p.inviteeId}/name-correction`, body: { use: 'ours' } })
                    }
                  >
                    Keep ours
                  </Button>
                </span>
              </td>
            </tr>
          ))}
          {hotelOnly.map((r) => (
            <tr key={r.key} className={stackRow}>
              <td className={`${stackCell} font-semibold`}>{r.nameOnList}</td>
              <td className={stackCell} data-label="Difference">
                <Col>Difference</Col>
                On the hotel’s list only
              </td>
              <td className={stackCell} data-label="Here">
                <Col>Here</Col>
                {dash}
              </td>
              <td className={stackCell} data-label="Hotel">
                <Col>Hotel</Col>
                {hotelDates(r.checkIn, r.checkOut) ?? dash}
              </td>
              <td className={actCell}>
                <span className="inline-flex flex-wrap items-center justify-end gap-2 max-md:justify-start">
                  <select
                    className="border border-line-strong rounded-sm bg-white px-2 min-h-[36px] text-[15px] text-ink max-w-[220px]"
                    aria-label={`Who is ${r.nameOnList} on the hotel’s list`}
                    value={linkTo[r.key] ?? ''}
                    onChange={(e) => setLinkTo({ ...linkTo, [r.key]: e.target.value })}
                  >
                    <option value="">This is…</option>
                    {[...people]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((p) => (
                        <option key={p.inviteeId} value={p.inviteeId}>
                          {p.name}
                        </option>
                      ))}
                  </select>
                  <Button
                    size="sm"
                    disabled={!linkTo[r.key] || act.isLoading}
                    onClick={() =>
                      act.mutate({
                        path: `/events/${eventId}/hotel/returned/link`,
                        body: { line: r.key.slice('hotel:'.length), inviteeId: linkTo[r.key] },
                      })
                    }
                  >
                    Link
                  </Button>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-small text-ink-3 mt-3 max-w-[72ch]">
        Accepting takes the hotel’s dates; it never removes a room. A member’s name is changed on Member 360; the
        hotel’s spelling is applied to contacts only.
      </p>
    </div>
  );
}

const ANSWER: Record<IncreaseRequest['answer'], string> = {
  pending: 'Waiting for the hotel',
  granted: 'Granted',
  partly: 'Partly granted',
  declined: 'Declined',
};
const ANSWER_TONE: Record<IncreaseRequest['answer'], 'running' | 'healthy' | 'inactive'> = {
  pending: 'running',
  granted: 'healthy',
  partly: 'healthy',
  declined: 'inactive',
};

function IncreaseRequests({
  eventId,
  view,
  canEdit,
  onSaved,
}: {
  eventId: string;
  view: HotelView;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const list = view.hotel.increaseRequests;
  const [rooms, setRooms] = useState('');
  const save = useMutation(
    async (next: IncreaseRequest[]) =>
      (await api.put(`/events/${eventId}/hotel/settings`, { increaseRequests: next })).data.data,
    {
      onSuccess: () => {
        setRooms('');
        onSaved();
      },
    },
  );
  const today = new Date().toISOString().slice(0, 10);
  if (!canEdit && !list.length) return null;
  const answer = async (i: number, r: IncreaseRequest, a: IncreaseRequest['answer']) => {
    const given = a === 'partly' ? await promptAction(`How many of the ${r.rooms} rooms did the hotel give?`) : null;
    if (a === 'partly' && !given) return;
    save.mutate(
      list.map((x, j) =>
        j === i ? { ...x, answer: a, answeredOn: today, ...(given ? { grantedRooms: Number(given) } : {}) } : x,
      ),
    );
  };
  return (
    <Panel>
      <PanelHeader title="Asking the hotel for more rooms" />
      <PanelBody>
        {list.length ? (
          <table className={`${stackTable} max-w-[820px]`}>
            <thead className="max-md:hidden">
              <tr className={headRow}>
                <th className={th}>Asked</th>
                <th className={`${th} ${num}`}>Rooms</th>
                <th className={th}>Answer</th>
              </tr>
            </thead>
            <tbody className="max-md:block">
              {list.map((r, i) => (
                <tr key={i} className={stackRow}>
                  <td className={`${stackCell} max-md:font-semibold`}>
                    {r.requestedOn ? monthDay(r.requestedOn) : <span className="text-ink-3">–</span>}
                  </td>
                  <td className={`${stackCell} ${num} max-md:text-left`} data-label="Rooms">
                    <Col>Rooms</Col>
                    {r.rooms}
                  </td>
                  <td className={stackCell} data-label="Answer">
                    <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      <Pill tone={ANSWER_TONE[r.answer]}>
                        {r.grantedRooms && r.answer !== 'pending'
                          ? `${ANSWER[r.answer]}, ${r.grantedRooms} added to the block`
                          : ANSWER[r.answer]}
                      </Pill>
                      {canEdit && r.answer === 'pending'
                        ? (['granted', 'partly', 'declined'] as const).map((a) => (
                            <Button
                              key={a}
                              size="sm"
                              variant="quiet"
                              className="px-0"
                              disabled={save.isLoading}
                              onClick={() => answer(i, r, a)}
                            >
                              {ANSWER[a]}
                            </Button>
                          ))
                        : null}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {save.error ? (
          <div className="mt-3">
            <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice>
          </div>
        ) : null}
        {canEdit ? (
          <div className={`flex flex-wrap items-center gap-3 ${list.length ? 'mt-4' : ''}`}>
            <label htmlFor="hotel-more-rooms" className="text-[15px] font-semibold text-ink">
              Rooms to ask for
            </label>
            <input
              id="hotel-more-rooms"
              className={`${inputClass} !w-[120px]`}
              inputMode="numeric"
              value={rooms}
              onChange={(e) => setRooms(e.target.value)}
            />
            <Button
              disabled={!Number(rooms) || save.isLoading}
              onClick={() =>
                save.mutate([
                  ...list,
                  { rooms: Number(rooms), requestedOn: today, answer: 'pending', answeredOn: null, note: null },
                ])
              }
            >
              I asked the hotel
            </Button>
          </div>
        ) : null}
      </PanelBody>
    </Panel>
  );
}

function stayText(p: HotelPerson) {
  if (!p.stay) return p.room.none ? 'No room needed' : '–';
  return `${monthDay(p.stay.checkIn)} – ${monthDay(p.stay.checkOut)} (${p.stay.nights.length})`;
}

type Filter = 'all' | 'flagged' | 'changed';
const FILTERS: [Filter, string][] = [
  ['all', 'Everyone'],
  ['flagged', 'To check'],
  ['changed', 'Not sent yet'],
];

function PeopleAndRooms({
  view,
  canEdit,
  filter,
  setFilter,
  night,
  setNight,
  onOpen,
}: {
  view: HotelView;
  canEdit: boolean;
  filter: Filter;
  setFilter: (f: Filter) => void;
  night: string | null;
  setNight: (d: string | null) => void;
  onOpen: (p: HotelPerson) => void;
}) {
  const [all, setAll] = useState(false);
  const people = (view.people ?? []).filter(
    (p) =>
      (!night || p.stay?.nights.includes(night)) &&
      (filter === 'all' ||
        (filter === 'flagged'
          ? p.flags.length > 0 || p.onList === 'dates_differ' || p.onList === 'missing'
          : p.sent !== 'yes' && p.stay)),
  );
  const shown = all ? people : people.slice(0, PEOPLE_SHOWN);
  const dash = <span className="text-ink-3">–</span>;
  return (
    <Panel>
      <div id="hotel-people" className="scroll-mt-6" />
      <PanelHeader
        title="People and rooms"
        note={`${view.counts.withRoom} with a room · ${view.counts.flagged} to check`}
      >
        <div
          role="group"
          aria-label="Show"
          className="inline-flex rounded-sm border border-line-strong overflow-hidden"
        >
          {FILTERS.map(([key, text], i) => (
            <button
              key={key}
              type="button"
              aria-pressed={filter === key}
              onClick={() => setFilter(key)}
              className={`min-h-[36px] px-3 text-[15px] leading-5 ${i ? 'border-l border-line-strong' : ''} ${
                filter === key ? 'bg-ink text-paper font-semibold' : 'text-ink hover:bg-selected'
              }`}
            >
              {text}
            </button>
          ))}
        </div>
      </PanelHeader>
      <PanelBody>
        {night ? (
          <p className="text-[15px] text-ink-2 mb-3">
            Staying the night of {weekDay(night)}.{' '}
            <button
              type="button"
              className="text-accent underline underline-offset-[3px] hover:text-ink"
              onClick={() => setNight(null)}
            >
              Show every night
            </button>
          </p>
        ) : null}
        {!people.length ? (
          <EmptyState title="Nobody here.">
            {filter === 'all' && !night
              ? 'Rooms appear when invitees ask for a hotel on the form, or when you set dates for someone.'
              : 'Nobody matches. Choose Everyone to see all the rooms.'}
          </EmptyState>
        ) : (
          <table className={stackTable}>
            <thead className="max-md:hidden">
              <tr className={headRow}>
                <th className={th}>Name</th>
                <th className={th}>Role</th>
                <th className={th}>Stay</th>
                <th className={th}>Room</th>
                <th className={th}>To check</th>
                <th className={th}>Sent</th>
                <th className={th}>Hotel’s list</th>
              </tr>
            </thead>
            <tbody className="max-md:block">
              {shown.map((p) => {
                const room = [
                  p.room.hotel === 'alternate' ? 'overflow hotel' : null,
                  p.room.roomType || (p.stay ? 'Single' : null),
                  p.room.sharesWith ? 'sharing' : null,
                  p.room.guest ? 'with guest' : null,
                  p.room.confirmed ? 'confirmed' : null,
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <tr key={p.inviteeId} className={`${stackRow} hover:bg-selected`}>
                    <td className={stackCell}>
                      {canEdit ? (
                        <button
                          type="button"
                          className="text-left text-accent underline underline-offset-[3px] hover:text-ink font-semibold"
                          onClick={() => onOpen(p)}
                        >
                          {p.name}
                        </button>
                      ) : (
                        <span className="font-semibold">{p.name}</span>
                      )}
                      {!p.expected ? <span className={em}>{label(p.status)}</span> : null}
                    </td>
                    <td className={stackCell} data-label="Role">
                      <Col>Role</Col>
                      {p.role ?? dash}
                    </td>
                    <td className={stackCell} data-label="Stay">
                      <Col>Stay</Col>
                      {stayText(p)}
                      {p.stay?.source === 'default' ? <span className={em}>default dates</span> : null}
                    </td>
                    <td className={stackCell} data-label="Room">
                      <Col>Room</Col>
                      {room || dash}
                    </td>
                    <td className={stackCell} data-label="To check">
                      {p.flags.length ? (
                        <>
                          <Col>To check</Col>
                          {p.flags.map((f) => (
                            <span key={f.rule} className="block">
                              <Pill tone="problem" small>
                                {f.message}
                              </Pill>
                            </span>
                          ))}
                        </>
                      ) : (
                        <span className="max-md:hidden">{dash}</span>
                      )}
                    </td>
                    <td className={stackCell} data-label="Sent">
                      <Col>Sent</Col>
                      {p.stay ? (p.sent === 'yes' ? 'Yes' : p.sent === 'changed' ? 'Changed since' : 'No') : dash}
                    </td>
                    <td className={stackCell} data-label="Hotel’s list">
                      <Col>Hotel’s list</Col>
                      {p.onList ? (
                        <Pill tone={ON_LIST[p.onList][1]} small>
                          {ON_LIST[p.onList][0]}
                        </Pill>
                      ) : (
                        dash
                      )}
                      {p.hotelList?.nameDiffers ? <span className={em}>as “{p.hotelList.nameOnList}”</span> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="text-[15px] leading-[22px] text-ink-3 mt-3 max-w-[72ch]">
          {people.length > PEOPLE_SHOWN && !all ? (
            <>
              <button
                type="button"
                className="text-accent underline underline-offset-[3px] hover:text-ink"
                onClick={() => setAll(true)}
              >
                Show all {people.length}
              </button>
              {' · '}
            </>
          ) : null}
          {canEdit ? 'Open a name to change their dates, room or notes. ' : ''}Default dates are the block nights (or,
          without a block, the night before for the nights the travel rules cover).
        </p>
      </PanelBody>
    </Panel>
  );
}

function leadSentence(v: HotelView, diff: ReturnType<typeof differences> | null) {
  const p = v.perNight;
  let s: string;
  if (!p.nights.length || !p.peakNight) s = 'No rooms on the list yet.';
  else if (p.blockSize === null || p.roomsLeft === null)
    s = `The busiest night is ${weekDay(p.peakNight)}, with ${p.peak} ${p.peak === 1 ? 'room' : 'rooms'}.`;
  else if (p.roomsLeft < 0)
    s = `The busiest night is ${weekDay(p.peakNight)}: ${p.peak} rooms, ${-p.roomsLeft} over the block of ${p.blockSize}.`;
  else
    s = `The busiest night is ${weekDay(p.peakNight)}: ${p.peak} of the ${p.blockSize} rooms, with ${p.roomsLeft} to spare.`;
  if (diff?.noneHere)
    s += ` The hotel’s own list has ${diff.noneHere} ${diff.noneHere === 1 ? 'person' : 'people'} with a room who ${
      diff.noneHere === 1 ? 'has' : 'have'
    } none here.`;
  else if (diff?.total)
    s += ` ${diff.total} ${diff.total === 1 ? 'difference' : 'differences'} with the hotel’s list to settle.`;
  return s;
}

export default function HotelPanel({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const hotel = useQuery<HotelView>(
    ['event-hotel', eventId],
    async () => (await api.get(`/events/${eventId}/hotel`)).data.data,
  );
  const [open, setOpen] = useState<HotelPerson | null>(null);
  const [editing, setEditing] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [night, setNight] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries(['event-hotel', eventId]);
  if (hotel.isLoading) return <SkeletonRows rows={6} />;
  if (hotel.error || !hotel.data)
    return <ErrorNotice>{errorMessage(hotel.error, 'Could not load the hotel.')}</ErrorNotice>;
  const v = hotel.data;
  const diff = v.peopleVisible && v.returned ? differences(v) : null;
  const changes = v.changeCounts.added + v.changeCounts.changed + v.changeCounts.removed;
  const over = v.perNight.roomsLeft !== null && v.perNight.roomsLeft < 0 ? -v.perNight.roomsLeft : 0;
  const showNight =
    v.peopleVisible && (v.people ?? []).length
      ? (date: string) => {
          setNight(date);
          setFilter('all');
          document.getElementById('hotel-people')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      : null;
  return (
    <div>
      <Lead
        lead={leadSentence(v, diff)}
        sub={`Rooms per night at ${v.hotel.name ? `the ${v.hotel.name}` : 'the hotel'}, the rooming list you send the hotel, and the list the hotel sends back.`}
      />
      {over ? (
        <div className="mt-4">
          <ErrorNotice>
            {over} {over === 1 ? 'room is' : 'rooms are'} over the block on the busiest night. Ask the hotel for more
            rooms below, or move people to an overflow hotel.
          </ErrorNotice>
        </div>
      ) : null}
      <RoomsPerNight view={v} canEdit={canEdit} onEdit={() => setEditing(true)} onNight={showNight} />
      {!v.peopleVisible ? (
        <div className="mt-8">
          <InfoNotice>
            Names and room details are for admins.{' '}
            {v.lastSent
              ? `Rooming list last sent ${when(v.lastSent.sentAt)} (${v.lastSent.rows} rooms); ${changes} ${changes === 1 ? 'change' : 'changes'} since.`
              : 'No rooming list sent yet.'}
          </InfoNotice>
        </div>
      ) : (
        <>
          <Panel>
            <PanelHeader
              title="Rooming list for the hotel"
              note={sentNote(v.lastSent?.sentAt ?? null, v.changes?.sentBefore ? changes : 0, when)}
            />
            <PanelBody>
              {canEdit ? (
                <div className="max-w-[820px]">
                  <SendSteps
                    download={`/events/${eventId}/hotel/rooming-list`}
                    markSent={`/events/${eventId}/hotel/mark-sent`}
                    to={v.hotel.contactEmail ?? 'the hotel'}
                    what="rooming list"
                    lastSent={v.lastSent?.sentAt ?? null}
                    onSent={refresh}
                    primary={false}
                  />
                </div>
              ) : null}
              {v.changes ? <ChangesTable changes={v.changes} labels={FIELD_LABELS} /> : null}
            </PanelBody>
          </Panel>
          {v.hotel.alternate || v.counts.alternate ? (
            <Panel>
              <PanelHeader
                title={`Overflow hotel: ${v.hotel.alternate?.name ?? 'not set'}`}
                note={`${v.counts.alternate} ${v.counts.alternate === 1 ? 'room' : 'rooms'}`}
              />
              <PanelBody>
                {!v.hotel.alternate ? (
                  <div className="mb-4">
                    <ErrorNotice>
                      Some rooms point to an overflow hotel that isn’t saved. Add it in Edit hotel details, or move
                      those rooms back to the block hotel.
                    </ErrorNotice>
                  </div>
                ) : null}
                {v.alternatePerNight ? (
                  <p className="text-[15px] text-ink mb-3">
                    {v.alternatePerNight.nights.map((n) => `${weekDay(n.date)}: ${n.rooms}`).join(' · ') ||
                      'No nights yet.'}
                  </p>
                ) : null}
                {canEdit && v.hotel.alternate ? (
                  <div className="max-w-[820px]">
                    <SendSteps
                      download={`/events/${eventId}/hotel/rooming-list?hotel=alternate`}
                      markSent={`/events/${eventId}/hotel/mark-sent?hotel=alternate`}
                      to={v.hotel.alternate.contactEmail ?? 'the overflow hotel'}
                      what="overflow rooming list"
                      lastSent={null}
                      onSent={refresh}
                      primary={false}
                    />
                  </div>
                ) : null}
              </PanelBody>
            </Panel>
          ) : null}
          {canEdit ? <ReturnedList eventId={eventId} view={v} onDone={refresh} /> : null}
          <IncreaseRequests eventId={eventId} view={v} canEdit={canEdit} onSaved={refresh} />
          <PeopleAndRooms
            view={v}
            canEdit={canEdit}
            filter={filter}
            setFilter={setFilter}
            night={night}
            setNight={setNight}
            onOpen={setOpen}
          />
        </>
      )}
      {editing ? (
        <HotelDetailsModal
          eventId={eventId}
          view={v}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            refresh();
          }}
        />
      ) : null}
      {open ? (
        <RoomModal
          eventId={eventId}
          person={open}
          sharers={v.sharers ?? []}
          alternate={v.hotel.alternate?.name ?? null}
          onClose={() => setOpen(null)}
          onSaved={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}
